import { useState, useEffect, useContext, useCallback } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import {
  FiActivity,
  FiAlertCircle,
  FiCalendar,
  FiCheckCircle,
  FiClock,
  FiEye,
  FiFile,
  FiPlus,
  FiSearch,
  FiSend,
  FiX,
  FiXCircle
} from "react-icons/fi";
import "../../../assets/css/doctor/doctorpages/DisputeViewResponse.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";

const CATEGORIES = ["Billing", "Service", "Technical", "Policy", "Other"];
const PRIORITIES = ["High", "Medium", "Low"];

const DisputeViewResponse = () => {
  const { baseUrl } = useContext(AppContext);
  const token = localStorage.getItem("doctor_token");
  const authHeaders = { Authorization: `Bearer ${token}`, Accept: "application/json" };

  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedDispute, setSelectedDispute] = useState(null);

  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newDispute, setNewDispute] = useState({
    subject: "",
    category: "Service",
    priority: "Medium",
    issue: "",
    amount: "",
  });
  const [submitting, setSubmitting] = useState(false);

  // ── Fetch disputes ───────────────────────────────────────────
  const fetchDisputes = useCallback(() => {
    setLoading(true);
    axios.get(`${baseUrl}/doctor/Dispute/my`, { headers: authHeaders })
      .then((res) => setDisputes(res.data.data || []))
      .catch((err) => console.error("Disputes load error:", err))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl]);

  useEffect(() => {
    fetchDisputes();
  }, [fetchDisputes]);

  const stats = {
    total: disputes.length,
    pending: disputes.filter((d) => d.status === "pending").length,
    resolved: disputes.filter((d) => d.status === "resolved").length,
    rejected: disputes.filter((d) => d.status === "rejected").length,
  };

  const handleViewDispute = (dispute) => {
    setSelectedDispute(dispute);
    setShowViewModal(true);
  };

  const handleCreateDispute = () => {
    if (!newDispute.subject.trim() || !newDispute.issue.trim()) {
      Swal.fire({ icon: "warning", title: "Missing Fields", text: "Please fill in the subject and issue description." });
      return;
    }
    setSubmitting(true);
    axios.post(`${baseUrl}/doctor/Dispute/create`, newDispute, { headers: authHeaders })
      .then(() => {
        Swal.fire({ icon: "success", title: "Submitted", text: "Your dispute has been submitted.", timer: 2000, showConfirmButton: false });
        setShowCreateModal(false);
        setNewDispute({ subject: "", category: "Service", priority: "Medium", issue: "", amount: "" });
        fetchDisputes();
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: "Failed", text: err?.response?.data?.message || "Something went wrong." });
      })
      .finally(() => setSubmitting(false));
  };

  const filteredDisputes = disputes.filter((dispute) => {
    const q = searchTerm.toLowerCase();
    const matchesSearch =
      (dispute.subject || "").toLowerCase().includes(q) ||
      (dispute.dispute_id || "").toLowerCase().includes(q) ||
      (dispute.patient?.name || "").toLowerCase().includes(q);
    const matchesFilter = filterStatus === "all" || dispute.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const getStatusClass = (status) => {
    switch (status) {
      case "pending":
        return "dvr-status-pending";
      case "resolved":
        return "dvr-status-resolved";
      case "rejected":
        return "dvr-status-rejected";
      default:
        return "";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return <FiClock />;
      case "resolved":
        return <FiCheckCircle />;
      case "rejected":
        return <FiXCircle />;
      default:
        return null;
    }
  };

  const getPriorityClass = (priority) => {
    switch ((priority || "").toLowerCase()) {
      case "high":
        return "dvr-priority-high";
      case "medium":
        return "dvr-priority-medium";
      case "low":
        return "dvr-priority-low";
      default:
        return "";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  };

  const filterTabs = [
    { key: "all", label: "All", count: stats.total },
    { key: "pending", label: "Pending", count: stats.pending },
    { key: "resolved", label: "Resolved", count: stats.resolved },
    { key: "rejected", label: "Rejected", count: stats.rejected },
  ];

  return (
    <DoctorMasterLayout>
      <div className="dvr-body">
        <div className="dvr-container">
          {/* Header */}
          <div className="dvr-header">
            <div className="dvr-header-left">
              <h2 className="dvr-title">Dispute View & Response</h2>
              <p className="dvr-subtitle">Track your disputes and raise new ones if needed</p>
            </div>
            <button className="dvr-btn-create" onClick={() => setShowCreateModal(true)}>
              <FiPlus /> Raise a Dispute
            </button>
          </div>

          {/* Stats Cards */}
          <div className="dvr-stats-cards">
            <div className="dvr-stat-card">
              <div className="dvr-stat-icon-box">
                <FiAlertCircle className="dvr-stat-icon" />
              </div>
              <div className="dvr-stat-info">
                <h3 className="dvr-stat-value">{stats.total}</h3>
                <p className="dvr-stat-label">Total Disputes</p>
              </div>
            </div>

            <div className="dvr-stat-card">
              <div className="dvr-stat-icon-box">
                <FiClock className="dvr-stat-icon" />
              </div>
              <div className="dvr-stat-info">
                <h3 className="dvr-stat-value">{stats.pending}</h3>
                <p className="dvr-stat-label">Pending</p>
              </div>
            </div>

            <div className="dvr-stat-card">
              <div className="dvr-stat-icon-box">
                <FiCheckCircle className="dvr-stat-icon" />
              </div>
              <div className="dvr-stat-info">
                <h3 className="dvr-stat-value">{stats.resolved}</h3>
                <p className="dvr-stat-label">Resolved</p>
              </div>
            </div>

            <div className="dvr-stat-card">
              <div className="dvr-stat-icon-box">
                <FiXCircle className="dvr-stat-icon" />
              </div>
              <div className="dvr-stat-info">
                <h3 className="dvr-stat-value">{stats.rejected}</h3>
                <p className="dvr-stat-label">Rejected</p>
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="dvr-filter-tabs-section">
            {filterTabs.map((tab) => (
              <button
                key={tab.key}
                className={`dvr-filter-tab ${filterStatus === tab.key ? "dvr-filter-tab-active" : ""}`}
                onClick={() => setFilterStatus(tab.key)}
              >
                <span className={`dvr-filter-dot dvr-filter-dot-${tab.key}`} />
                {tab.label}
                <span className="dvr-filter-count">{tab.count}</span>
              </button>
            ))}
          </div>

          {/* Table Section */}
          <div className="dvr-table-section">
            <div className="dvr-section-head">
              <h3 className="dvr-section-title">All Disputes</h3>
              <div className="dvr-search-box">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Search by subject or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>

            <div className="dvr-table-wrapper">
              {loading ? (
                <div className="dvr-no-data">Loading disputes...</div>
              ) : filteredDisputes.length > 0 ? (
                <table className="dvr-table">
                  <thead>
                    <tr>
                      <th>Dispute ID</th>
                      <th>Subject</th>
                      <th>Category</th>
                      <th>Priority</th>
                      <th>Amount</th>
                      <th>Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDisputes.map((dispute) => (
                      <tr key={dispute.id}>
                        <td>
                          <span className="dvr-id-cell">
                            <FiFile /> {dispute.dispute_id}
                          </span>
                        </td>
                        <td>
                          <div className="dvr-subject-cell">
                            <strong>{dispute.subject}</strong>
                            <p>{dispute.issue}</p>
                          </div>
                        </td>
                        <td>
                          <span className="dvr-category-pill">
                            <FiActivity /> {dispute.category}
                          </span>
                        </td>
                        <td>
                          <span className={`dvr-priority-badge ${getPriorityClass(dispute.priority)}`}>
                            {dispute.priority}
                          </span>
                        </td>
                        <td>
                          <span className="dvr-amount-cell">{dispute.amount ? `Rs. ${dispute.amount}` : "N/A"}</span>
                        </td>
                        <td>
                          <span className="dvr-date-cell">
                            <FiCalendar /> {formatDate(dispute.raised_on || dispute.created_at)}
                          </span>
                        </td>
                        <td>
                          <span className={`dvr-status-badge ${getStatusClass(dispute.status)}`}>
                            {getStatusIcon(dispute.status)} {dispute.status}
                          </span>
                        </td>
                        <td>
                          <div className="dvr-actions">
                            <button
                              className="dvr-action-btn"
                              onClick={() => handleViewDispute(dispute)}
                              title="View Details"
                            >
                              <FiEye />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="dvr-no-data">
                  <FiAlertCircle />
                  No disputes found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── View Dispute Modal ─────────────────────────────────── */}
      {showViewModal && selectedDispute && (
        <div className="dvr-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="dvr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="dvr-modal-header">
              <div className="dvr-modal-title-group">
                <h3 className="dvr-modal-title">Dispute Details - {selectedDispute.dispute_id}</h3>
                <span className={`dvr-status-badge ${getStatusClass(selectedDispute.status)}`}>
                  {getStatusIcon(selectedDispute.status)} {selectedDispute.status}
                </span>
              </div>
              <button className="dvr-modal-close" onClick={() => setShowViewModal(false)}>
                <FiX />
              </button>
            </div>

            <div className="dvr-modal-body">
              {/* Dispute Info */}
              <div className="dvr-detail-section">
                <h4 className="dvr-detail-heading">Dispute Information</h4>
                <div className="dvr-detail-grid">
                  <div className="dvr-detail-item">
                    <span className="dvr-detail-label">Patient Name</span>
                    <span className="dvr-detail-text">{selectedDispute.patient?.name || "N/A"}</span>
                  </div>
                  <div className="dvr-detail-item">
                    <span className="dvr-detail-label">Category</span>
                    <span className="dvr-detail-text">{selectedDispute.category}</span>
                  </div>
                  <div className="dvr-detail-item">
                    <span className="dvr-detail-label">Created Date</span>
                    <span className="dvr-detail-text">{formatDate(selectedDispute.raised_on || selectedDispute.created_at)}</span>
                  </div>
                  <div className="dvr-detail-item">
                    <span className="dvr-detail-label">Priority</span>
                    <span className={`dvr-priority-badge ${getPriorityClass(selectedDispute.priority)}`}>
                      {selectedDispute.priority}
                    </span>
                  </div>
                  {selectedDispute.amount && (
                    <div className="dvr-detail-item dvr-full">
                      <span className="dvr-detail-label">Amount Involved</span>
                      <span className="dvr-detail-text dvr-amount-large">Rs. {selectedDispute.amount}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Subject & Description */}
              <div className="dvr-detail-section">
                <h4 className="dvr-detail-heading">Subject & Description</h4>
                <div className="dvr-detail-grid">
                  <div className="dvr-detail-item dvr-full">
                    <span className="dvr-detail-label">Subject</span>
                    <span className="dvr-detail-text">{selectedDispute.subject}</span>
                  </div>
                  <div className="dvr-detail-item dvr-full">
                    <span className="dvr-detail-label">Description</span>
                    <p className="dvr-detail-text dvr-body-text">{selectedDispute.issue}</p>
                  </div>
                </div>
              </div>

              {/* Admin Resolution */}
              {selectedDispute.status !== "pending" && selectedDispute.resolution && (
                <div className="dvr-detail-section dvr-response-section">
                  <h4 className="dvr-detail-heading">Admin's Response</h4>
                  <p className="dvr-detail-text dvr-body-text">{selectedDispute.resolution}</p>
                  {selectedDispute.resolved_at && (
                    <p className="dvr-detail-meta">Resolved on {formatDate(selectedDispute.resolved_at)}</p>
                  )}
                </div>
              )}

              {selectedDispute.status === "pending" && (
                <div className="dvr-detail-section">
                  <p className="dvr-pending-note">Your dispute is under review. Admin will respond soon.</p>
                </div>
              )}
            </div>

            <div className="dvr-modal-footer">
              <button className="dvr-btn-cancel" onClick={() => setShowViewModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create Dispute Modal ───────────────────────────────── */}
      {showCreateModal && (
        <div className="dvr-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="dvr-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="dvr-modal-header">
              <h3 className="dvr-modal-title">Raise a Dispute</h3>
              <button className="dvr-modal-close" onClick={() => setShowCreateModal(false)}>
                <FiX />
              </button>
            </div>
            <div className="dvr-modal-body">
              <div className="dvr-form-group">
                <label>Subject <span className="dvr-required">*</span></label>
                <input
                  type="text"
                  placeholder="Brief summary of the issue"
                  value={newDispute.subject}
                  onChange={(e) => setNewDispute({ ...newDispute, subject: e.target.value })}
                />
              </div>

              <div className="dvr-form-row">
                <div className="dvr-form-group">
                  <label>Category <span className="dvr-required">*</span></label>
                  <select
                    value={newDispute.category}
                    onChange={(e) => setNewDispute({ ...newDispute, category: e.target.value })}
                  >
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="dvr-form-group">
                  <label>Priority <span className="dvr-required">*</span></label>
                  <select
                    value={newDispute.priority}
                    onChange={(e) => setNewDispute({ ...newDispute, priority: e.target.value })}
                  >
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>

              <div className="dvr-form-group">
                <label>Amount Involved (optional)</label>
                <input
                  type="number"
                  placeholder="e.g. 3000"
                  value={newDispute.amount}
                  onChange={(e) => setNewDispute({ ...newDispute, amount: e.target.value })}
                />
              </div>

              <div className="dvr-form-group">
                <label>Issue Description <span className="dvr-required">*</span></label>
                <textarea
                  rows="5"
                  placeholder="Describe the issue in detail..."
                  value={newDispute.issue}
                  onChange={(e) => setNewDispute({ ...newDispute, issue: e.target.value })}
                />
              </div>
            </div>
            <div className="dvr-modal-footer">
              <button className="dvr-btn-cancel" onClick={() => setShowCreateModal(false)} disabled={submitting}>
                Cancel
              </button>
              <button className="dvr-btn-send" onClick={handleCreateDispute} disabled={submitting}>
                <FiSend /> {submitting ? "Submitting..." : "Submit Dispute"}
              </button>
            </div>
          </div>
        </div>
      )}
    </DoctorMasterLayout>
  );
};

export default DisputeViewResponse;